import { nonce } from '../utils/runtime';
import { isTerminalReceiptRedemption } from '../utils/receiptRedemptionStatus';

const RECEIPT_REDEMPTIONS_BASE = '/wp-json/pfandpirat/v1/receipt-redemptions';
const RECEIPT_STATUS_TIMEOUT_MS = 30000;
const RECEIPT_STATUS_POLL_MS = 1000;

function sleep(ms) {
  return new Promise(resolve => window.setTimeout(resolve, ms));
}

async function parseResponse(response) {
  const text = await response.text();
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return text;
  }
}

function duplicateReceiptId(data) {
  return Number(data?.data?.duplicate_of || data?.duplicate_of || 0);
}

export async function loadReceiptRedemption(id) {
  const response = await fetch(`${RECEIPT_REDEMPTIONS_BASE}/${Number(id)}`, {
    credentials: 'include',
    headers: { 'X-WP-Nonce': nonce }
  });
  const data = await parseResponse(response);
  if (!response.ok) {
    const error = new Error(data?.message || `HTTP ${response.status}`);
    error.status = response.status;
    error.payload = data;
    throw error;
  }
  return data;
}

export async function waitForReceiptRedemptionTerminal(initialResult, {
  loadItem = loadReceiptRedemption,
  wait = sleep,
  timeoutMs = RECEIPT_STATUS_TIMEOUT_MS,
  pollMs = RECEIPT_STATUS_POLL_MS,
  onProgress = null,
  now = () => Date.now(),
} = {}) {
  let current = initialResult;
  if (typeof onProgress === 'function') onProgress(current);
  if (isTerminalReceiptRedemption(current)) return current;

  const id = Number(current?.id || 0);
  if (!id) return { ...(current || {}), client_status_timeout: true };

  const startedAt = now();
  let refreshFailed = false;
  while (now() - startedAt < timeoutMs) {
    await wait(pollMs);
    try {
      current = await loadItem(id);
      refreshFailed = false;
      if (typeof onProgress === 'function') onProgress(current);
      if (isTerminalReceiptRedemption(current)) return current;
    } catch {
      refreshFailed = true;
    }
  }

  return {
    ...(current || {}),
    client_status_timeout: true,
    client_status_refresh_failed: refreshFailed,
  };
}

async function postReceipt(formData) {
  const response = await fetch(RECEIPT_REDEMPTIONS_BASE, {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-WP-Nonce': nonce },
    body: formData
  });
  const data = await parseResponse(response);

  if (response.status === 409 && duplicateReceiptId(data) > 0) {
    const existing = await loadReceiptRedemption(duplicateReceiptId(data));
    return { ...existing, client_duplicate_replay: true };
  }

  if (!response.ok) {
    const error = new Error(data?.message || data?.error || `HTTP ${response.status}`);
    error.status = response.status;
    error.payload = data;
    throw error;
  }
  if (Number(data?.duplicate_of || 0) > 0) {
    const existing = await loadReceiptRedemption(Number(data.duplicate_of));
    return { ...existing, client_duplicate_replay: true };
  }
  return data;
}

export async function uploadReceiptRedemption(file, {
  mode = 'match_existing',
  scope = 'personal',
  crewId = null,
  selectedEntryIds = [],
  position = null,
  onProgress = null,
} = {}) {
  const formData = new FormData();
  formData.append('receipt', file);
  formData.append('mode', mode);
  formData.append('scope', scope);
  if (scope === 'crew' && crewId) {
    formData.append('crew_id', String(crewId));
  }
  if (Array.isArray(selectedEntryIds) && selectedEntryIds.length) {
    formData.append('selected_entry_ids', JSON.stringify(selectedEntryIds.map(Number).filter(Boolean)));
  }
  if (position?.latitude != null && position?.longitude != null) {
    formData.append('latitude', String(position.latitude));
    formData.append('longitude', String(position.longitude));
    if (position.accuracy != null) {
      formData.append('accuracy', String(position.accuracy));
    }
  }

  let data;
  try {
    data = await postReceipt(formData);
  } catch (error) {
    if (Number(error?.status || 0) > 0) throw error;
    await sleep(500);
    data = await postReceipt(formData);
  }

  return waitForReceiptRedemptionTerminal(data, { onProgress });
}

export async function loadReceiptRedemptions() {
  const response = await fetch(RECEIPT_REDEMPTIONS_BASE, {
    credentials: 'include',
    headers: { 'X-WP-Nonce': nonce }
  });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  const data = await response.json();
  return Array.isArray(data) ? data : [];
}

export async function updateReceiptRedemptionBeta(enabled) {
  const response = await fetch(`${RECEIPT_REDEMPTIONS_BASE}/beta`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-WP-Nonce': nonce
    },
    body: JSON.stringify({ enabled: Boolean(enabled) })
  });

  const text = await response.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }

  if (!response.ok) {
    const message =
      (data && typeof data === 'object' && (data.message || data.error)) ||
      `HTTP ${response.status}`;
    const error = new Error(message);
    error.status = response.status;
    error.payload = data;
    throw error;
  }

  return data;
}
